function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

if (!customElements.get("cart-drawer-disclosure")) {
  class CartDrawerDisclosure extends HTMLElement {
    constructor() {
      super();
      this.onSummaryClick = this.onSummaryClick.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
    }

    connectedCallback() {
      
      this.details = this.querySelector("details");
      if (!this.details) return;

      this.summary = this.details.querySelector("summary");
      this.popupContent = this.details.querySelector(".cart-drawer-popup__content");
      this.closeBtn = this.details.querySelector(".js-close");
      this.cartDrawer = document.getElementById("cart-drawer");
      this.overlay = this.cartDrawer ? this.cartDrawer.querySelector(".overlay") : null;

      
      this.cleanup?.();

      
      this.summary?.addEventListener("click", this.onSummaryClick, true);
      this.details.addEventListener("keyup", this.onKeyUp);
      this.overlay?.addEventListener("click", this.onOverlayClick);

      
      this.details.addEventListener("click", (e) => {
        if (e.target && e.target.closest(".js-close")) {
          e.preventDefault();
          this.close();
        }
      });

     
      this.cleanup = () => {
        this.summary?.removeEventListener("click", this.onSummaryClick, true);
        this.details?.removeEventListener("keyup", this.onKeyUp);
        this.overlay?.removeEventListener("click", this.onOverlayClick);
      };
    }

    disconnectedCallback() {
      this.cleanup?.();
    }

    onSummaryClick(evt) {
     
      evt.preventDefault();
      evt.stopImmediatePropagation();

      if (this.details.open) this.close();
      else this.open();
    }

    onOverlayClick(evt) {
      
      evt.preventDefault();
      this.close();
    }

    onKeyUp(evt) {
      if (evt.key === "Escape") this.close();
    }

    open() {
      if (!this.details) return;

      this.details.open = true;

      if (this.overlay) {
        this.overlay.classList.add("is-visible");
        this.overlay.style.pointerEvents = "auto";
      }

      // focus textarea first if present
      const textarea = this.popupContent?.querySelector("textarea");
      if (textarea) textarea.focus();

      // trap focus inside popup content (best target)
      const trapTarget = this.popupContent || this.details;
      try {
        trapFocus(trapTarget, textarea || trapTarget);
      } catch (e) {
        trapFocus(trapTarget);
      }
    }

    close() {
      if (!this.details) return;

      this.details.open = false;

      if (this.overlay) {
        this.overlay.classList.remove("is-visible");
        this.overlay.style.pointerEvents = "none";
      }

      // remove popup trap and restore focus to "Add order note" trigger (summary)
      try {
        removeTrapFocus(this.summary);
      } catch (e) {
        // ignore if signature differs
      }

      if (this.summary) this.summary.focus({ preventScroll: true });

      // restore cart drawer trap AFTER focus is placed (prevents focus jump + click eating)
      if (this.cartDrawer && this.cartDrawer.getAttribute("aria-hidden") === "false") {
        requestAnimationFrame(() => {
          try {
            trapFocus(this.cartDrawer, this.summary);
          } catch (e) {
            trapFocus(this.cartDrawer);
          }
        });
      }
    }
  }

  customElements.define("cart-drawer-disclosure", CartDrawerDisclosure);
}


if (!customElements.get("cart-drawer")) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();
      this.cartSummary = this.querySelector(".cart-drawer__summary");
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener("dispatch:cart-drawer:refresh", this.refreshHandler);
      document.removeEventListener("on:bfcache:load-restore", this.refreshHandler);
      document.removeEventListener("dispatch:cart-drawer:open", this.openDrawerViaEventHandler);
      document.removeEventListener("dispatch:cart-drawer:close", this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener("shopify:section:select", this.sectionSelectHandler);
        document.removeEventListener("shopify:section:deselect", this.sectionDeselectHandler);
      }

      this.dcbLoadedHandler &&
        document.removeEventListener("shopify:payment_button:loaded", this.dcbLoadedHandler);
    }

    init() {
      const cartIcon = document.getElementById("cart-icon");
      if (cartIcon) {
        cartIcon.setAttribute("role", "button");
        cartIcon.setAttribute("aria-haspopup", "dialog");

        cartIcon.addEventListener("click", (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener("keydown", (evt) => {
          evt.key === " " && (evt.preventDefault(), this.open(cartIcon));
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener("shopify:section:select", this.sectionSelectHandler);
        document.addEventListener("shopify:section:deselect", this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener("dispatch:cart-drawer:open", this.openDrawerViaEventHandler);
      document.addEventListener("dispatch:cart-drawer:close", this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains("cart-drawer--checkout--sticky-true")) {
        this.dcbLoadedHandler =
          this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener("shopify:payment_button:loaded", this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener("dispatch:cart-drawer:refresh", this.refreshHandler);
      document.addEventListener("on:bfcache:load-restore", this.refreshHandler);
    }

    handleSectionSelect(evt) {
      evt.target === this.closest(".shopify-section") && this.open();
    }

    handleSectionDeselect(evt) {
      evt.target === this.closest(".shopify-section") && this.close();
    }

    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    open(opener, elementToFocus) {
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]'),
        overlay = document.querySelector(".js-overlay.is-visible");

      if (quickAddDrawer) {
        overlay && (overlay.style.transitionDelay = "200ms");
        quickAddDrawer.close();
      }

      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          this.cartSummary.classList.contains("cart-drawer--checkout--sticky-true") &&
            CartDrawer.recalculateCssVarHeights();
        });

        this.cartSummary.classList.contains("cart-drawer--checkout--sticky-true") &&
          CartDrawer.recalculateCssVarHeights();

        overlay && (overlay.style.transitionDelay = "");
      }, quickAddDrawer ? 200 : 0);
    }

    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector("cart-items");
        if (cartItems && !dontRefreshCartItems) cartItems.refresh();
        else {
          const response = this.getSectionsToRender().map((section) => section.section),
            sections = await (await fetch(`?sections=${response.join(",")}`)).json();

          this.renderContents({ sections }, !1);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error);
        this.dispatchEvent(
          new CustomEvent("on:cart:error", {
            bubbles: !0,
            detail: { error: this.errorMsg?.textContent },
          })
        );
      }
    }

    async renderContents(data, openDrawer = !0) {
      if (!this.scriptsLoaded) {
        document.querySelector(`script[src="${theme.scripts.cartItems}"]`) ||
          (await loadScript(theme.scripts.cartItems));

        if (
          !document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) &&
          this.dataset.shippingCalculator
        ) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = !0;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        el &&
          (el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          ));
      });

      openDrawer &&
        this.getAttribute("open") === null &&
        setTimeout(() => this.open());
    }

    getSectionsToRender() {
      return [
        {
          id: "cart-drawer",
          section: this.closest(".shopify-section").id.replace("shopify-section-", ""),
          selector: "cart-drawer",
        },
        { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
      ];
    }

    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent("on:css-var-height:update"));
      });
    }

    static getElementHTML(html, selector) {
      const tmpl = document.createElement("template");
      tmpl.innerHTML = html;
      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define("cart-drawer", CartDrawer);
}

//# sourceMappingURL=/s/files/1/0929/5911/3490/t/6/assets/cart-drawer.js.map?v=1741200965
